import { useState, useCallback, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useTickets,
  useTicketStats,
  useDeleteTicket,
  useBulkDeleteTickets,
  useBulkUpdateTickets,
} from '@/hooks/use-api'
import { usePagination } from '@/hooks/use-pagination'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { RefreshButton } from '@/components/RefreshButton'
import { SearchWithRecent } from '@/components/ui/search-with-recent'
import { Badge } from '@/components/ui/badge'
import { ResizableTable } from '@/components/ui/resizable-table'
import { toDisplayDate } from '@/lib/utils'
import { CATEGORICAL } from '@/lib/theme'
import {
  Headphones,
  Plus,
  AlertCircle,
  Clock,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
} from 'lucide-react'
import { BulkActionsBar } from '@/components/BulkActionsBar'
import { BulkDeleteDialog } from '@/components/BulkDeleteDialog'
import { BulkUpdateModal, type BulkUpdateField } from '@/components/BulkUpdateModal'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { useReadOnly } from '@/hooks/use-read-only'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useUser } from '@/contexts/UserContext'
import { usePersistentState, makeFilterKey } from '@/hooks/use-persistent-state'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { createTicketsColumns, ticketsDefaultColumnSizing } from '@/config/columns/tickets-columns'
import { explainError } from '@/lib/explain-error'

const ALL_STATUSES = [
  'New',
  'Open',
  'Pending',
  'On Hold',
  'Escalated',
  'Resolved',
  'Closed',
] as const

export default function Tickets() {
  const navigate = useNavigate()
  const { currentUser, impersonatedUser, canManageTickets } = useUser()
  const fk = makeFilterKey(currentUser?.id, 'tickets')
  const [search, setSearch] = useState('')
  const [searchField, setSearchField] = usePersistentState<string>(fk('searchField'), 'all')
  // Status filter persisted as an array (Sets aren't JSON-serializable).
  const [statusList, setStatusList] = usePersistentState<string[]>(fk('statuses'), [
    ...ALL_STATUSES,
  ])
  // Migrate saved filters from before 'Escalated' existed: a persisted list with
  // every legacy status selected meant "all statuses" — keep it meaning "all",
  // otherwise those users would silently start excluding Escalated tickets.
  useEffect(() => {
    const legacyFull = ['New', 'Open', 'Pending', 'On Hold', 'Resolved', 'Closed']
    if (
      statusList.length === legacyFull.length &&
      legacyFull.every((s) => statusList.includes(s))
    ) {
      setStatusList([...ALL_STATUSES])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const selectedStatuses = useMemo(() => new Set(statusList), [statusList])
  const setSelectedStatuses = useCallback(
    (next: Set<string> | ((prev: Set<string>) => Set<string>)) =>
      setStatusList((prevArr) => {
        const resolved = typeof next === 'function' ? next(new Set(prevArr)) : next
        return Array.from(resolved)
      }),
    [setStatusList]
  )
  const { page, firstPage, prevPage, nextPage, extract } = usePagination()
  const [showMyTickets, setShowMyTickets] = usePersistentState(fk('showMy'), false)
  const [sortBy, setSortBy] = usePersistentState(fk('sortBy'), 'created_date')
  const [sortOrder, setSortOrder] = usePersistentState<'asc' | 'desc'>(fk('sortOrder'), 'desc')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [ticketToDelete, setTicketToDelete] = useState<{ id: string; subject: string } | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false)

  const { data, isLoading } = useTickets({
    search,
    search_field: searchField !== 'all' ? searchField : undefined,
    page,
    owner_id: showMyTickets ? impersonatedUser?.id || currentUser?.id : undefined,
    status:
      selectedStatuses.size < ALL_STATUSES.length
        ? Array.from(selectedStatuses).join(',')
        : undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
  })
  const { data: stats } = useTicketStats()
  const deleteMutation = useDeleteTicket()
  const bulkDeleteMutation = useBulkDeleteTickets()
  const bulkUpdateMutation = useBulkUpdateTickets()
  const { toast } = useToast()
  const isReadOnly = useReadOnly()

  const { items: tickets, total, currentPage, limit, hasNextPage, hasPrevPage } = extract(data)

  const handleSort = useCallback(
    (field: string) => {
      if (sortBy === field) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortBy(field)
        setSortOrder('asc')
      }
      firstPage()
    },
    [sortBy, firstPage]
  )

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!ticketToDelete) return

    try {
      await deleteMutation.mutateAsync(ticketToDelete.id)

      toast({
        title: 'Success',
        description: `Ticket "${ticketToDelete.subject}" has been deleted.`,
      })

      setDeleteDialogOpen(false)
      setTicketToDelete(null)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to delete ticket')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle selection
  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleToggleSelectAll = () => {
    if (selectedIds.size === tickets.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(tickets.map((ticket) => ticket.id)))
    }
  }

  const handleClearSelection = () => {
    setSelectedIds(new Set())
  }

  // Handle bulk delete
  const handleBulkDelete = async () => {
    try {
      const idsArray = Array.from(selectedIds)
      await bulkDeleteMutation.mutateAsync(idsArray)

      toast({
        title: 'Success',
        description: `${idsArray.length} ticket${idsArray.length === 1 ? '' : 's'} deleted successfully.`,
      })

      setBulkDeleteDialogOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to delete tickets')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle bulk update
  const handleBulkUpdate = async (updates: Record<string, any>) => {
    try {
      await bulkUpdateMutation.mutateAsync({ ids: Array.from(selectedIds), updates })

      toast({
        title: 'Success',
        description: `${selectedIds.size} ticket${selectedIds.size === 1 ? '' : 's'} updated successfully.`,
      })

      setBulkUpdateModalOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to update tickets')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Define field configurations for bulk update
  const ticketsUpdateFields: BulkUpdateField[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'New', label: 'New' },
        { value: 'Open', label: 'Open' },
        { value: 'Pending', label: 'Pending' },
        { value: 'On Hold', label: 'On Hold' },
        { value: 'Resolved', label: 'Resolved' },
        { value: 'Closed', label: 'Closed' },
      ],
    },
    {
      key: 'priority',
      label: 'Priority',
      type: 'select',
      options: [
        { value: 'Low', label: 'Low' },
        { value: 'Medium', label: 'Medium' },
        { value: 'High', label: 'High' },
        { value: 'Urgent', label: 'Urgent' },
      ],
    },
  ]

  const formatDate = (date?: string) => {
    if (!date) return '-'
    return (toDisplayDate(date) ?? new Date(date)).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const columns = useMemo(
    () =>
      createTicketsColumns({
        selectedIds,
        onToggleSelect: handleToggleSelect,
        onToggleSelectAll: handleToggleSelectAll,
        allSelected: selectedIds.size === tickets.length && tickets.length > 0,
        formatDate,
        // Read-only roles (Sales/Marketing/RFP) can't bulk-edit; hide selection.
        enableSelection: canManageTickets,
      }),
    [selectedIds, tickets.length, canManageTickets]
  )

  return (
    <div className="space-y-4" role="main" aria-label="Tickets management">
      {canManageTickets && selectedIds.size > 0 && (
        <BulkActionsBar
          selectedCount={selectedIds.size}
          entityType="ticket"
          onClearSelection={handleClearSelection}
          onBulkDelete={() => setBulkDeleteDialogOpen(true)}
          onBulkUpdate={() => setBulkUpdateModalOpen(true)}
          isDeleting={bulkDeleteMutation.isPending}
          isUpdating={bulkUpdateMutation.isPending}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Badge
              variant="outline"
              style={{
                backgroundColor: CATEGORICAL.navy.tint,
                color: CATEGORICAL.navy.solid,
                borderColor: CATEGORICAL.navy.solid,
              }}
            >
              <Headphones className="mr-1.5 h-3 w-3" aria-hidden="true" />
              Support
            </Badge>
            <h1 className="text-3xl font-bold tracking-tight">Tickets</h1>
          </div>
          <p className="text-muted-foreground mt-1">Track and manage support tickets</p>
        </div>
        <div className="flex gap-2">
          <RefreshButton queryKey={['tickets']} label="tickets" />
          {canManageTickets && (
            <Button
              aria-label="Create new ticket"
              onClick={() => navigate('/tickets/new')}
              disabled={isReadOnly}
              title={isReadOnly ? 'Cannot create tickets while viewing as another user' : undefined}
            >
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              New Ticket
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-2 md:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Open</CardTitle>
            <Clock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              {(stats?.new_count ?? 0) + (stats?.open_count ?? 0)}
            </div>
            <p className="text-xs text-muted-foreground">New + Open tickets</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <AlertCircle className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              {(stats?.pending_count ?? 0) + (stats?.on_hold_count ?? 0)}
            </div>
            <p className="text-xs text-muted-foreground">Waiting for response</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Resolved</CardTitle>
            <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{stats?.resolved_count ?? 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Urgent</CardTitle>
            <AlertCircle className="h-3.5 w-3.5 text-destructive" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none text-destructive">
              {stats?.urgent_count ?? 0}
            </div>
            <p className="text-xs text-muted-foreground">
              High priority: {stats?.high_priority_count ?? 0}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">SLA Breached</CardTitle>
            <AlertTriangle className="h-3.5 w-3.5 text-destructive" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none text-destructive">
              {stats?.sla_breached_count ?? 0}
            </div>
            <p className="text-xs text-muted-foreground">Needs attention</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{showMyTickets ? 'My Tickets' : 'All Tickets'}</CardTitle>
          <CardDescription>View and manage support tickets</CardDescription>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <Tabs
              defaultValue="my"
              value={showMyTickets ? 'my' : 'all'}
              onValueChange={(value) => {
                setShowMyTickets(value === 'my')
                firstPage()
              }}
            >
              <TabsList>
                <TabsTrigger value="all" aria-label="Show all tickets">
                  All Tickets
                </TabsTrigger>
                <TabsTrigger value="my" aria-label="Show only my tickets">
                  My Tickets
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="w-[170px] justify-between"
                  aria-label={`Filter by status. ${selectedStatuses.size === ALL_STATUSES.length ? 'All statuses selected' : `${selectedStatuses.size} of ${ALL_STATUSES.length} statuses selected`}`}
                >
                  {selectedStatuses.size === ALL_STATUSES.length
                    ? 'All Statuses'
                    : selectedStatuses.size === 0
                      ? 'No Status'
                      : `Status (${selectedStatuses.size})`}
                  <ChevronDown className="ml-2 h-4 w-4 opacity-50" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[170px]">
                <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {ALL_STATUSES.map((status) => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={selectedStatuses.has(status)}
                    onCheckedChange={(checked) => {
                      setSelectedStatuses((prev) => {
                        const next = new Set(prev)
                        if (checked) {
                          next.add(status)
                        } else {
                          next.delete(status)
                        }
                        return next
                      })
                      firstPage()
                    }}
                  >
                    {status}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuCheckboxItem
                  checked={selectedStatuses.size === ALL_STATUSES.length}
                  onCheckedChange={(checked) => {
                    setSelectedStatuses(checked ? new Set(ALL_STATUSES) : new Set())
                    firstPage()
                  }}
                >
                  Select All
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <div
              className="flex items-center border rounded-md"
              role="group"
              aria-label="Search tickets"
            >
              <Select value={searchField} onValueChange={setSearchField}>
                <SelectTrigger
                  className="w-[130px] border-0 rounded-r-none border-r shadow-none focus:ring-0"
                  aria-label="Search field"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Fields</SelectItem>
                  <SelectItem value="subject">Subject</SelectItem>
                  <SelectItem value="account">Account</SelectItem>
                  <SelectItem value="contact">Contact</SelectItem>
                  <SelectItem value="product">Product</SelectItem>
                  <SelectItem value="jira">Jira</SelectItem>
                </SelectContent>
              </Select>
              <SearchWithRecent
                value={search}
                onChange={setSearch}
                placeholder={
                  searchField === 'subject'
                    ? 'Search by subject...'
                    : searchField === 'account'
                      ? 'Search by account...'
                      : searchField === 'contact'
                        ? 'Search by contact...'
                        : searchField === 'product'
                          ? 'Search by product...'
                          : searchField === 'jira'
                            ? 'Search by Jira key...'
                            : 'Search tickets...'
                }
                ariaLabel={`Search tickets${searchField !== 'all' ? ` by ${searchField}` : ''}`}
                entityKey="tickets"
                className="[&_input]:border-0 [&_input]:shadow-none [&_input]:focus-visible:ring-0"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div
              className="text-center py-8 text-muted-foreground"
              role="status"
              aria-live="polite"
            >
              Loading tickets...
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No tickets found. Create your first ticket to get started.
            </div>
          ) : (
            <>
              <ResizableTable
                columns={columns}
                data={tickets}
                storageKey="tickets-column-widths"
                defaultColumnSizing={ticketsDefaultColumnSizing}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, total)}{' '}
                  of {total} tickets
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={prevPage}
                    disabled={!hasPrevPage}
                    aria-label="Previous page"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={nextPage}
                    disabled={!hasNextPage}
                    aria-label="Next page"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Ticket</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{ticketToDelete?.subject}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={bulkDeleteDialogOpen}
        onOpenChange={setBulkDeleteDialogOpen}
        selectedCount={selectedIds.size}
        entityType="ticket"
        onConfirm={handleBulkDelete}
        isDeleting={bulkDeleteMutation.isPending}
      />

      {/* Bulk Update Modal */}
      <BulkUpdateModal
        open={bulkUpdateModalOpen}
        onOpenChange={setBulkUpdateModalOpen}
        selectedCount={selectedIds.size}
        entityType="ticket"
        fields={ticketsUpdateFields}
        onConfirm={handleBulkUpdate}
        isUpdating={bulkUpdateMutation.isPending}
      />
    </div>
  )
}
